import { WHITE } from "@chesshotel/chess";
import {
  ChessTerm,
  ChessTermType,
  MoveType,
  TermAction,
} from "@chesshotel/types";
import { Translate } from "next-translate";

export function getKeyAndValues(
  chessTerm: ChessTerm,
  moveType: string,
  t: Translate,
  fen: string
) {
  function deterministicPick(arr, seed) {
    if (!Array.isArray(arr)) return arr;
    let hash = 0;
    if (typeof seed === "string") {
      for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) % arr.length;
      }
    } else if (typeof seed === "number") {
      hash = seed % arr.length;
    } else {
      hash = 0;
    }
    return arr[Math.abs(hash) % arr.length];
  }

  if (chessTerm.term == ChessTermType.NONE) {
    const moveTypeKeys = commentKeys[chessTerm.term];

    const keys = moveTypeKeys[moveType];

    const i18nKey = Array.isArray(keys) ? deterministicPick(keys, fen) : keys;

    return { i18nKey, values: null };
  }

  const termActionKeys = commentKeys[chessTerm.term];

  if (!termActionKeys) {
    return { i18nKey: null, values: null };
  }

  const i18nKeyArray = termActionKeys[chessTerm.termAction];

  if (!i18nKeyArray) {
    return { i18nKey: null, values: null };
  }

  const i18nKey = Array.isArray(i18nKeyArray)
    ? deterministicPick(i18nKeyArray, fen)
    : i18nKeyArray;

  const values = getDynamicValues(chessTerm, t);

  return { i18nKey, values };
}

const commentKeys = {
  /* ===== KICK_PIECE ===== */
  [ChessTermType.KICK_PIECE]: {
    [TermAction.PERFORM]: [
      "kickPiecePerform1",
      "kickPiecePerform2",
      "kickPiecePerform3",
    ],
    [TermAction.ALLOW]: ["kickPieceAllow1", "kickPieceAllow2"],
    [TermAction.MISS]: ["kickPieceMiss1", "kickPieceMiss2"],
    [TermAction.BETTER_WAY]: ["kickPieceBetterWay1"],
  },

  /* ===== TEMPO ===== */
  [ChessTermType.TEMPO]: {
    [TermAction.PERFORM]: ["tempoPerform1"],
    [TermAction.ALLOW]: ["tempoAllow1"],
    [TermAction.MISS]: ["tempoMiss1"],
    [TermAction.BETTER_WAY]: ["tempoBetterWay1"],
  },

  /* ===== THREATEN_PIECE ===== */
  [ChessTermType.THREATEN_PIECE]: {
    [TermAction.PERFORM]: [
      "threatenPiecePerform1",
      "threatenPiecePerform2",
      "threatenPiecePerform3",
      "threatenPiecePerform4",
    ],
    [TermAction.ALLOW]: ["threatenPieceAllow1", "threatenPieceAllow2"],
    [TermAction.MISS]: ["threatenPieceMiss1"],
    [TermAction.BETTER_WAY]: ["threatenPieceBetterWay1"],
  },

  /* ===== THREATEN_TRADE ===== */
  [ChessTermType.THREATEN_TRADE]: {
    [TermAction.PERFORM]: ["threatenTradePerform1", "threatenTradePerform2"],
    [TermAction.ALLOW]: ["threatenTradeAllow1"],
    [TermAction.MISS]: ["threatenTradeMiss1"],
  },

  /* ===== THREATEN_CHECKMATE ===== */
  [ChessTermType.THREATEN_CHECKMATE]: {
    [TermAction.PERFORM]: ["threatenCheckmatePerform1"],
    [TermAction.ALLOW]: ["threatenCheckmateAllow1"],
    [TermAction.MISS]: ["threatenCheckmateMiss1"],
    [TermAction.POSPONE]: ["threatenCheckmatePospone1"],
    [TermAction.BETTER_WAY]: ["threatenCheckmateBetterWay1"],
  },

  /* ===== OFFER_TRADE_SAME_PIECE ===== */
  [ChessTermType.OFFER_TRADE_SAME_PIECE]: {
    [TermAction.PERFORM]: ["offerTradeSamePiecePerform1"],
    [TermAction.ALLOW]: ["offerTradeSamePieceAllow1"],
    [TermAction.MISS]: ["offerTradeSamePieceMiss1", "offerTradeSamePieceMiss2"],
    [TermAction.BETTER_WAY]: ["offerTradeSamePieceBetterWay1"],
  },
  /* ===== OFFER_TRADE_PASSED_PAWN ===== */
  [ChessTermType.OFFER_TRADE_PASSED_PAWN]: {
    [TermAction.PERFORM]: ["offerTradePassedPawnPerform1"],
    [TermAction.ALLOW]: ["offerTradePassedPawnAllow1"],
    [TermAction.MISS]: ["offerTradePassedPawnMiss1"],
  },

  /* ===== OFFER_TRADE_CONNECT_PAWN ===== */
  [ChessTermType.OFFER_TRADE_CONNECT_PAWN]: {
    [TermAction.PERFORM]: ["offerTradeConnectPawnPerform1"],
    [TermAction.ALLOW]: ["offerTradeConnectPawnAllow1"],
    [TermAction.MISS]: ["offerTradeConnectPawnMiss1"],
  },

  /* ===== OFFER_TRADE ===== */
  [ChessTermType.OFFER_TRADE]: {
    [TermAction.PERFORM]: ["offerTradePerform1"],
    [TermAction.ALLOW]: ["offerTradeAllow1"],
    [TermAction.MISS]: ["offerTradeMiss1"],
    [TermAction.BETTER_WAY]: ["offerTradeBetterWay1"],
  },

  /* ===== MATERIAL_WIN ===== */
  [ChessTermType.MATERIAL_WIN]: {
    [TermAction.PERFORM]: ["materialWinPerform1", "materialWinPerform2"],
    [TermAction.ALLOW]: ["materialWinAllow1"],
    [TermAction.MISS]: ["materialWinMiss1"],
    [TermAction.BETTER_WAY]: ["materialWinBetterWay1"],
  },

  /* ===== DEFEND_MATE_CHECK ===== */
  [ChessTermType.DEFEND_MATE_CHECK]: {
    [TermAction.PERFORM]: ["defendMateCheckPerform1"],
  },

  /* ===== DIRECT_MATERIAL_WIN ===== */
  [ChessTermType.DIRECT_MATERIAL_WIN]: {
    [TermAction.PERFORM]: [
      "directMaterialWinPerform1",
      "directMaterialWinPerform2",
    ],
    [TermAction.ALLOW]: ["directMaterialWinAllow1"],
    [TermAction.MISS]: ["directMaterialWinMiss1", "directMaterialWinMiss2"],
    [TermAction.BETTER_WAY]: ["directMaterialWinBetterWay1"],
  },

  [ChessTermType.HANGING]: {
    [TermAction.PERFORM]: ["hangingPiecePerform1", "hangingPiecePerform2"],
    [TermAction.ALLOW]: ["hangingAllow1", "hangingAllow2"],
    [TermAction.MISS]: ["hangingPieceMiss1", "hangingPieceMiss2"],
    [TermAction.BETTER_WAY]: ["hangingPieceBetterWay1"],
  },

  /* ===== TACTICS START ===== */
  /* ===== TACTIC THREAT ===== */
  // [ChessTermType.TACTIC_THREAT]: {
  //   [TermAction.PERFORM]: ["This threatens to win material through a tactic"],
  //   [TermAction.ALLOW]: [],
  //   [TermAction.MISS]: [],
  // },

  /* ===== TACTIC ===== */
  [ChessTermType.TACTIC]: {
    [TermAction.PERFORM]: ["tacticPerform1"],
    [TermAction.ALLOW]: ["tacticAllow1"],
    [TermAction.MISS]: ["tacticMiss1"],
    [TermAction.PERFORM_THREAT]: ["tacticPerformThreat1"],
    [TermAction.BETTER_WAY]: ["tacticBetterWay1"],
  },

  /* ===== FORK ===== */
  [ChessTermType.FORK]: {
    [TermAction.PERFORM]: ["forkPerform1", "forkPerform2"],
    [TermAction.ALLOW]: ["forkAllow1", "forkAllow2"],
    [TermAction.MISS]: ["forkMiss1"],
    [TermAction.BETTER_WAY]: ["forkBetterWay1"],
    [TermAction.PERFORM_THREAT]: ["forkPerformThreat1"],
  },

  /* ===== PIN ===== */
  [ChessTermType.PIN]: {
    [TermAction.PERFORM]: ["pinPerform1"],
    [TermAction.ALLOW]: ["pinAllow1"],
    [TermAction.MISS]: ["pinMiss1"],
    [TermAction.PERFORM_THREAT]: ["pinPerformThreat1"],
  },

  /* ===== SKEWER ===== */
  [ChessTermType.SKEWER]: {
    [TermAction.PERFORM]: ["skewerPerform1"],
    [TermAction.ALLOW]: ["skewerAllow1"],
    [TermAction.MISS]: ["skewerMiss1", "skewerMiss2"],
    [TermAction.PERFORM_THREAT]: ["skewerPerformThreat1"],
  },

  /* ===== DOUBLE_ATTACK ===== */
  [ChessTermType.DOUBLE_ATTACK]: {
    [TermAction.PERFORM]: ["doubleAttackPerform1"],
    [TermAction.ALLOW]: ["doubleAttackAllow1"],
    [TermAction.MISS]: ["doubleAttackMiss1"],
    [TermAction.PERFORM_THREAT]: ["doubleAttackPerformThreat1"],
  },

  /* ===== DISCOVERED_ATTACK ===== */
  [ChessTermType.DISCOVERED_ATTACK]: {
    [TermAction.PERFORM]: ["discoveredAttackPerform1"],
    [TermAction.ALLOW]: ["discoveredAttackAllow1"],
    [TermAction.MISS]: ["discoveredAttackMiss1"],
    [TermAction.PERFORM_THREAT]: ["discoveredAttackPerformThreat1"],
    [TermAction.ALLOW_THREAT]: ["discoveredAttackAllowThreat1"],
    [TermAction.MISS_THREAT]: ["discoveredAttackMissThreat1"],
  },

  /* ===== UNPIN ===== */
  [ChessTermType.UNPIN]: {
    [TermAction.PERFORM]: ["unpinPerform1"],
    [TermAction.ALLOW]: ["unpinAllow1", "unpinAllow2"],
    [TermAction.MISS]: ["unpinMiss1"],
  },

  /* ===== REMOVE_DEFENDER ===== */
  [ChessTermType.REMOVE_DEFENDER]: {
    [TermAction.PERFORM]: ["removeDefenderPerform1"],
    [TermAction.ALLOW]: ["removeDefenderAllow1"],
    [TermAction.MISS]: ["removeDefenderMiss1"],
    [TermAction.ALLOW_THREAT]: ["removeDefenderAllowThreat1"],
    [TermAction.MISS_THREAT]: ["removeDefenderMissThreat1"],
    [TermAction.PERFORM_THREAT]: ["removeDefenderPerformThreat1"],
  },

  /* ===== OVERLOADING ===== */
  [ChessTermType.OVERLOADING]: {
    [TermAction.PERFORM]: ["overloadingPerform1"],
    [TermAction.ALLOW]: ["overloadingAllow1"],
    [TermAction.MISS]: ["overloadingMiss1"],
  },

  /* ===== DEFLECTION ===== */
  [ChessTermType.DEFLECTION]: {
    [TermAction.PERFORM]: ["deflectionPerform1"],
    [TermAction.ALLOW]: ["deflectionAllow1"],
    [TermAction.MISS]: ["deflectionMiss1"],
  },

  /* ===== TRAPPED_PIECE ===== */
  [ChessTermType.TRAPPED_PIECE]: {
    [TermAction.PERFORM]: ["trappedPiecePerform1"],
    [TermAction.ALLOW]: ["trappedPieceAllow1", "trappedPieceAllow2"],
    [TermAction.MISS]: ["trappedPieceMiss1"],
  },

  /* ===== TACTICS END ===== */

  /* --- PAWN_CHAIN --- */
  [ChessTermType.PAWN_CHAIN]: {
    [TermAction.PERFORM]: ["pawnChainPerform1"],
    [TermAction.ALLOW]: ["pawnChainAllow1"],
    [TermAction.MISS]: ["pawnChainMiss1"],
    [TermAction.BETTER_WAY]: ["pawnChainBetterWay1"],
  },

  /* ===== DOUBLE_PAWN ===== */
  [ChessTermType.DOUBLE_PAWN]: {
    [TermAction.PERFORM]: ["doublePawnPerform1"],
    [TermAction.ALLOW]: ["doublePawnAllow1"],
    [TermAction.MISS]: ["doublePawnMiss1"],
    [TermAction.CREATE]: ["doublePawnCreate1"],
  },

  /* ===== FORCE_DOUBLE_PAWN ===== */
  [ChessTermType.FORCE_DOUBLE_PAWN]: {
    [TermAction.PERFORM]: ["forceDoublePawnPerform1"],
    [TermAction.ALLOW]: ["forceDoublePawnAllow1"],
    [TermAction.MISS]: ["forceDoublePawnMiss1"],
  },

  /* ===== BACKWARD_PAWN ===== */
  [ChessTermType.BACKWARD_PAWN]: {
    [TermAction.PERFORM]: ["backwardPawnPerform1"],
    [TermAction.ALLOW]: ["backwardPawnAllow1"],
    [TermAction.MISS]: ["backwardPawnMiss1"],
    [TermAction.CREATE]: ["backwardPawnCreate1"],
  },

  /* ===== ISOLATED_PAWN ===== */
  [ChessTermType.ISOLATED_PAWN]: {
    [TermAction.PERFORM]: ["isolatedPawnPerform1"],
    [TermAction.ALLOW]: ["isolatedPawnAllow1"],
    [TermAction.MISS]: ["isolatedPawnMiss1"],
    [TermAction.CREATE]: ["isolatedPawnCreate1"],
  },

  /* ===== FORCE_ISOLATE_PAWN ===== */
  [ChessTermType.FORCE_ISOLATE_PAWN]: {
    [TermAction.PERFORM]: ["forceIsolatePawnPerform1"],
    [TermAction.ALLOW]: ["forceIsolatePawnAllow1"],
  },

  /* ===== PASSED_PAWN ===== */
  [ChessTermType.PASSED_PAWN]: {
    [TermAction.PERFORM]: ["passedPawnPerform1"],
    [TermAction.ALLOW]: ["passedPawnAllow1"],
    [TermAction.MISS]: ["passedPawnMiss1"],
    [TermAction.CREATE]: ["passedPawnCreate1"],
  },

  /* ===== PROMOTION ===== */
  [ChessTermType.PROMOTION]: {
    [TermAction.PERFORM]: ["promotionPerform1"],
    [TermAction.ALLOW]: ["promotionAllow1"],
    [TermAction.MISS]: ["promotionMiss1"],
    [TermAction.BETTER_WAY]: ["promotionBetterWay1"],
  },

  /* ===== CONNECT_PAWN ===== */
  [ChessTermType.CONNECT_PAWN]: {
    [TermAction.PERFORM]: ["connectPawnPerform1"],
    [TermAction.ALLOW]: ["connectPawnAllow1"],
    [TermAction.MISS]: ["connectPawnMiss1"],
  },

  /* ===== IN_CHECK_BLOCK ===== */
  [ChessTermType.IN_CHECK_BLOCK]: {
    [TermAction.PERFORM]: ["inCheckBlockPerform1"],
    [TermAction.BETTER_WAY]: ["inCheckBlockBetterWay1"],
  },

  /* ===== IN_CHECK_MOVE_TO_SAFETY ===== */
  [ChessTermType.IN_CHECK_MOVE_TO_SAFETY]: {
    [TermAction.PERFORM]: ["inCheckMoveToSafetyPerform1"],
    [TermAction.BETTER_WAY]: ["inCheckMoveToSafetyBetterWay1"],
  },
  /* ===== IN_CHECK_ESCAPE ===== */
  [ChessTermType.IN_CHECK_ESCAPE]: {
    [TermAction.BETTER_WAY]: ["inCheckEscapeBetterWay1"],
  },

  /* ===== BLOCK ===== */
  [ChessTermType.BLOCK]: {
    [TermAction.PERFORM]: ["blockPerform1"],
    [TermAction.ALLOW]: ["blockAllow1"],
    [TermAction.MISS]: ["blockMiss1"],
  },

  /* ===== DEFEND ===== */
  [ChessTermType.DEFEND]: {
    [TermAction.PERFORM]: ["defendPerform1", "defendPerform2"],
    [TermAction.ALLOW]: ["defendAllow1", "defendAllow2"],
    [TermAction.MISS]: ["defendMiss1"],
    [TermAction.BETTER_WAY]: ["defendBetterWay1", "defendBetterWay2"],
  },

  /* ===== MOVE_TO_SAFETY ===== */
  [ChessTermType.MOVE_TO_SAFETY]: {
    [TermAction.PERFORM]: [
      "moveToSafetyPerform1",
      "moveToSafetyPerform2",
      "moveToSafetyPerform3",
    ],
    [TermAction.ALLOW]: ["moveToSafetyAllow1", "moveToSafetyAllow2"],
    [TermAction.MISS]: [
      "moveToSafetyMiss1",
      // "{{color}} leaves {{thePiece}} in danger instead of moving it to safety",
    ],
    [TermAction.BETTER_WAY]: ["moveToSafetyBetterWay1"],
  },

  /* ===== CENTER_CONTROL ===== */
  [ChessTermType.CENTER_CONTROL]: {
    [TermAction.PERFORM]: ["centerControlPerform1", "centerControlPerform2"],
    [TermAction.ALLOW]: ["centerControlAllow1", "centerControlAllow2"],
    [TermAction.MISS]: ["centerControlMiss1", "centerControlMiss2"],
  },

  /* ===== KING_SAFETY ===== */
  [ChessTermType.KING_SAFETY]: {
    [TermAction.PERFORM]: ["kingSafetyPerform1", "kingSafetyPerform2"],
    [TermAction.ALLOW]: ["kingSafetyAllow1", "kingSafetyAllow2"],
    [TermAction.MISS]: ["kingSafetyMiss1", "kingSafetyMiss2"],
  },

  /* ===== BAD_KING_SAFETY ===== */
  [ChessTermType.BAD_KING_SAFETY]: {
    [TermAction.PERFORM]: ["badKingSafetyPerform1", "badKingSafetyPerform2"],
  },

  /* ===== CASTLE ===== */
  [ChessTermType.CASTLE]: {
    [TermAction.PERFORM]: [
      "castlePerform1",
      "castlePerform2",
      "castlePerform3",
    ],
    [TermAction.MISS]: ["castleMiss1"],
    [TermAction.ALLOW]: ["castleAllow1"],
  },

  [ChessTermType.PREVENT_CASTLING]: {
    [TermAction.PERFORM]: ["preventCastlingPerform1"],
    [TermAction.ALLOW]: ["preventCastlingAllow1"],
    [TermAction.MISS]: ["preventCastlingMiss1"],
  },

  /* ===== OUTPOST ===== */
  [ChessTermType.OUTPOST]: {
    [TermAction.PERFORM]: [
      "outpostPerform1",
      "outpostPerform2",
      "outpostPerform3",
    ],
    [TermAction.ALLOW]: ["outpostAllow1"],
    [TermAction.MISS]: ["outpostMiss1"],
  },

  /* ===== PIECE_ACTIVITY ===== */
  [ChessTermType.PIECE_ACTIVITY]: {
    [TermAction.PERFORM]: ["pieceActivityPerform1", "pieceActivityPerform2"],
    [TermAction.ALLOW]: ["pieceActivityAllow1"],
    [TermAction.MISS]: ["pieceActivityMiss1"],
    [TermAction.BETTER_WAY]: ["pieceActivityBetterWay1"],
  },

  /********** OPEN_FILE **********/
  [ChessTermType.OPEN_FILE]: {
    [TermAction.PERFORM]: ["openFilePerform1"],
    [TermAction.ALLOW]: ["openFileAllow1"],
    [TermAction.MISS]: ["openFileMiss1"],
    [TermAction.BETTER_WAY]: ["openFileBetterWay1"],
  },

  /* ===== OPEN_DIAGONAL ===== */
  [ChessTermType.OPEN_DIAGONAL]: {
    [TermAction.PERFORM]: ["openDiagonalPerform1", "openDiagonalPerform2"],
    [TermAction.ALLOW]: ["openDiagonalAllow1", "openDiagonalAllow2"],
    [TermAction.MISS]: ["openDiagonalMiss1", "openDiagonalMiss2"],
  },

  /* ===== CHALLENGE_OPEN_FILE ===== */
  [ChessTermType.CHALLENGE_OPEN_FILE]: {
    [TermAction.PERFORM]: [
      "challengeOpenFilePerform1",
      "challengeOpenFilePerform2",
    ],
    [TermAction.ALLOW]: ["challengeOpenFileAllow1"],
    [TermAction.MISS]: ["challengeOpenFileMiss1", "challengeOpenFileMiss2"],
  },

  /* ===== CHALLENGE_OPEN_DIAGONAL ===== */
  [ChessTermType.CHALLENGE_OPEN_DIAGONAL]: {
    [TermAction.PERFORM]: [
      "challengeOpenDiagonalPerform1",
      "challengeOpenDiagonalPerform2",
    ],
    [TermAction.ALLOW]: ["challengeOpenDiagonalAllow1"],
    [TermAction.MISS]: ["challengeOpenDiagonalMiss1"],
  },

  /* ===== SACRIFICE ===== */
  [ChessTermType.SACRIFICE]: {
    [TermAction.PERFORM]: ["sacrificePerform1", "sacrificePerform2"],
    [TermAction.ALLOW]: ["sacrificeAllow1"],
    [TermAction.MISS]: ["sacrificeMiss1", "sacrificeMiss2"],
  },

  /* ===== UNDEFENDED_SACRIFICE ===== */
  [ChessTermType.UNDEFENDED_SACRIFICE]: {
    [TermAction.PERFORM]: ["undefendedSacrificePerform1"],
  },

  /* ===== IGNORE_THREAT ===== */
  [ChessTermType.IGNORE_THREAT]: {
    [TermAction.PERFORM]: ["ignoreThreatPerform1"],
  },

  /* ===== BOOK_MOVE ===== */
  [ChessTermType.BOOK_MOVE]: {
    [TermAction.PERFORM]: ["bookMovePerform1", "bookMovePerform2"],
    // Typically we do not have ALLOW/MISS for a book move
  },

  /* ===== DEVELOP_QUEEN_EARLY ===== */
  [ChessTermType.DEVELOP_QUEEN_EARLY]: {
    [TermAction.PERFORM]: [
      "developQueenEarlyPerform1",
      "developQueenEarlyPerform2",
    ],
    // Usually no ALLOW/MISS for developing queen early
  },

  /* ===== DEVELOPMENT ===== */
  [ChessTermType.DEVELOPMENT]: {
    [TermAction.PERFORM]: ["developmentPerform1"],
    [TermAction.MISS]: ["developmentMiss1", "developmentMiss2"],
    [TermAction.BETTER_WAY]: ["developmentBetterWay1"],
  },

  /* ===== ACTIVATE QUEEN ===== */
  [ChessTermType.ACTIVATE_QUEEN]: {
    [TermAction.PERFORM]: ["activateQueenPerform1"],
    [TermAction.MISS]: ["activateQueenMiss1"],
    [TermAction.BETTER_WAY]: ["activateQueenBetterWay1"],
  },

  /* ===== ACTIVATE ROOK ===== */
  [ChessTermType.ACTIVATE_ROOK]: {
    [TermAction.PERFORM]: ["activateRookPerform1"],
    [TermAction.MISS]: ["activateRookMiss1"],
    [TermAction.BETTER_WAY]: ["activateRookBetterWay1"],
  },

  /* ===== MOVE_DEVELOPED_PIECE ===== */
  [ChessTermType.MOVE_DEVELOPED_PIECE]: {
    [TermAction.PERFORM]: ["moveDevelopedPiecePerform1"],
  },

  /* ===== BAD_OPENING_MOVE ===== */
  [ChessTermType.BAD_OPENING_MOVE]: {
    [TermAction.PERFORM]: ["badOpeningMovePerform1", "badOpeningMovePerform2"],
  },

  /* ===== CHECKMATE ===== */
  [ChessTermType.CHECKMATE]: {
    [TermAction.PERFORM]: ["checkmatePerform1"],
    [TermAction.ALLOW]: ["checkmateAllow1", "checkmateAllow2"],
    [TermAction.MISS]: ["checkmateMiss1", "checkmateMiss2"],
  },

  /* ===== DRAW ===== */
  [ChessTermType.DRAW]: {
    [TermAction.PERFORM]: ["drawPerform1", "drawPerform2"],
  },

  /* ===== STALE_MATE ===== */
  [ChessTermType.STALE_MATE]: {
    [TermAction.PERFORM]: ["staleMatePerform1", "staleMatePerform2"],
    [TermAction.ALLOW]: ["staleMateAllow1", "staleMateAllow2"],
  },

  /* ===== TRADE ===== */
  [ChessTermType.TRADE]: {
    [TermAction.PERFORM]: ["tradePerform1"],
    [TermAction.BETTER_WAY]: ["tradeBetterWay1"],
    [TermAction.ALLOW]: ["tradeAllow1"],
    [TermAction.MISS]: ["tradeMiss1"],
  },

  /* ===== BAD_TRADE ===== */
  [ChessTermType.BAD_TRADE]: {
    [TermAction.PERFORM]: [
      "badTradePerform1",
      "badTradePerform2",
      "badTradePerform3",
    ],
  },

  /* ===== BAD_EQUAL_TRADE ===== */
  [ChessTermType.BAD_EQUAL_TRADE]: {
    [TermAction.PERFORM]: ["badEqualTradePerform1", "badEqualTradePerform2"],
  },

  /* ===== RETAKE ===== */
  [ChessTermType.RETAKE]: {
    [TermAction.PERFORM]: ["retakePerform1"],
    [TermAction.BETTER_WAY]: ["retakeBetterWay1"],
  },

  /* ===== TAKES_BACK ===== */
  [ChessTermType.TAKES_BACK]: {
    [TermAction.PERFORM]: ["takesBackPerform1"],
    [TermAction.BETTER_WAY]: ["takesBackBetterWay1"],
  },

  /********** CONNECT_ROOKS **********/
  [ChessTermType.CONNECT_ROOKS_BACK_RANK]: {
    [TermAction.PERFORM]: ["connectRooksBackRankPerform1"],
    [TermAction.ALLOW]: ["connectRooksAllow1"],
    [TermAction.MISS]: ["connectRooksMiss1"],
    [TermAction.BETTER_WAY]: ["connectRooksBetterWay1"],
  },
  [ChessTermType.CONNECT_ROOKS]: {
    [TermAction.PERFORM]: ["connectRooksPerform1"],
    [TermAction.ALLOW]: ["connectRooksAllow1"],
    [TermAction.MISS]: ["connectRooksMiss1"],
    [TermAction.BETTER_WAY]: ["connectRooksBetterWay1"],
  },

  /********** CONNECT_QUEEN_AND_BISHOP **********/
  [ChessTermType.CONNECT_QUEEN_AND_BISHOP]: {
    [TermAction.PERFORM]: [
      "connectQueenAndBishopPerform1",
      "connectQueenAndBishopPerform2",
    ],
    [TermAction.ALLOW]: ["connectQueenAndBishopAllow1"],
    [TermAction.MISS]: [
      "connectQueenAndBishopMiss1",
      "connectQueenAndBishopMiss2",
    ],
    [TermAction.BETTER_WAY]: ["connectQueenAndBishopBetterWay1"],
  },

  /********** ATTACK_PAWN_CHAIN **********/
  [ChessTermType.ATTACK_PAWN_CHAIN]: {
    [TermAction.PERFORM]: [
      "attackPawnChainPerform1",
      "attackPawnChainPerform2",
    ],
    [TermAction.ALLOW]: ["attackPawnChainAllow1", "attackPawnChainAllow2"],
    [TermAction.MISS]: ["attackPawnChainMiss1", "attackPawnChainMiss2"],
    [TermAction.BETTER_WAY]: ["attackPawnChainBetterWay1"],
  },

  /********** FOLLOW_CHECKMATE **********/
  [ChessTermType.FOLLOW_CHECKMATE]: {
    [TermAction.PERFORM]: [
      "followCheckmatePerform1",
      "followCheckmatePerform2",
      "followCheckmatePerform3",
    ],
    [TermAction.ALLOW]: ["followCheckmateAllow1"],
    [TermAction.MISS]: ["followCheckmateMiss1"],
    [TermAction.BETTER_WAY]: ["followCheckmateBetterWay1"],
  },

  /* ===== DEFEND_MATE ===== */
  [ChessTermType.DEFEND_MATE]: {
    [TermAction.PERFORM]: ["defendMatePerform1"],
    [TermAction.MISS]: ["defendMateMiss1", "defendMateMiss2"],
    [TermAction.BETTER_WAY]: ["defendMateBetterWay1"],
  },

  /********** COUNTER_ATTACK **********/
  [ChessTermType.COUNTER_ATTACK]: {
    [TermAction.PERFORM]: ["counterAttackPerform1"],
    [TermAction.ALLOW]: ["counterAttackAllow1"],
    [TermAction.MISS]: ["counterAttackMiss1"],
    [TermAction.BETTER_WAY]: ["counterAttackBetterWay1"],
  },

  /********** BLOCKADE **********/
  [ChessTermType.BLOCKADE]: {
    [TermAction.PERFORM]: ["blockadePerform1", "blockadePerform2"],
    [TermAction.ALLOW]: ["blockadeAllow1"],
    [TermAction.MISS]: ["blockadeMiss1", "blockadeMiss2"],
    [TermAction.BETTER_WAY]: ["blockadeBetterWay1"],
  },

  [ChessTermType.NONE]: {
    [MoveType.BLUNDER]: ["noneBlunder1", "noneBlunder2"],
    [MoveType.MISTAKE]: ["noneMistake1", "noneMistake2"],
    [MoveType.INACCURACY]: ["noneInaccuracy1", "noneInaccuracy2"],
    [MoveType.GOOD]: ["noneGood1", "noneGood2"],
    [MoveType.EXCELLENT]: [
      "noneExcellent1",
      "noneExcellent2",
      "noneExcellent3",
    ],
    [MoveType.BEST]: ["noneBest1", "noneBest2"],
    [MoveType.GREAT]: ["noneGreat1", "noneGreat2"],
    [MoveType.BRILLIANT]: ["noneBrilliant1"],
    [MoveType.BOOK]: ["noneBook1"],
  },

  [ChessTermType.CHECK]: {
    [TermAction.PERFORM]: ["checkPerform1"],
    [TermAction.ALLOW]: ["checkAllow1"],
    [TermAction.MISS]: ["checkMiss1"],
    [TermAction.BETTER_WAY]: ["checkBetterWay1"],
  },

  /* ===== ADVANTAGE_TO_EQUAL ===== */
  [ChessTermType.ADVANTAGE_TO_EQUAL]: {
    [TermAction.PERFORM]: [
      "advantageToEqualPerform1",
      "advantageToEqualPerform2",
    ],
    [TermAction.ALLOW]: ["advantageToEqualAllow1"],
  },

  /* ===== EQUAL_TO_ADVANTAGE ===== */
  [ChessTermType.EQUAL_TO_ADVANTAGE]: {
    [TermAction.PERFORM]: [
      "equalToAdvantagePerform1",
      "equalToAdvantagePerform2",
    ],
    [TermAction.ALLOW]: ["equalToAdvantageAllow1"],
  },

  /* ===== INITIATIVE_CHANGE ===== */
  [ChessTermType.INITIATIVE_CHANGE]: {
    [TermAction.PERFORM]: ["initiativeChangePerform1"],
    [TermAction.ALLOW]: ["initiativeChangeAllow1", "initiativeChangeAllow2"],
  },
};

function getDynamicValues(chessTerm: ChessTerm, t: Translate) {
  if (!chessTerm.translationKeys) return null;

  let translatedValues = {};
  for (const key in chessTerm.translationKeys) {
    let value = chessTerm.translationKeys[key];

    if (!value) {
      console.log("NO VALUE FOR", key, chessTerm);
    }

    if (key == "color") {
      translatedValues[key] = t(value == WHITE ? "white" : "black");
    }
    if (key == "piece") {
      translatedValues["piece"] = t(value);
      translatedValues["pieces"] = t(value + "s");
      translatedValues["thePiece"] = t("the_" + value);
      translatedValues["aPiece"] = t("a_" + value);
    }

    if (key == "piece2") {
      translatedValues["piece2"] = t(value);
      translatedValues["pieces2"] = t(value + "s");
      translatedValues["thePiece2"] = t("the_" + value);
      translatedValues["aPiece2"] = t("a_" + value);
    }

    if (key == "square") {
      translatedValues[key] = t(value);
    }

    if (key == "mateMoves") {
      translatedValues[key] = value;
    }

    if (key == "passedPawn") {
      translatedValues[key] = t(value);
    }
  }

  return translatedValues;
}

// export default function TranslatedComment({
//   chessTerm,
//   t,
// }: {
//   chessTerm: ChessTerm;
//   t: Translate;
// }) {
//   if (
//     chessTerm.term == ChessTermType.KICK_PIECE ||
//     chessTerm.term == ChessTermType.TEMPO ||
//     chessTerm.term == ChessTermType.THREATEN_PIECE
//   ) {
//     const termActions = threatComments[chessTerm.term];

//     if (!termActions) {
//       return;
//     }

//     // return t(termActions[chessTerm.termAction], {
//     //   piece: t(chessTerm.attackedPiece.type),
//     // });
//     return (
//       <Trans
//         i18nKey={termActions[chessTerm.termAction]}
//         values={{ piece: t(chessTerm.attackedPiece.type) }}
//         components={[<b />]}
//         ns="gameReview"
//       />
//     );
//   }
// }
